import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../../config';

const TOKEN_KEY = 'bb_admin_token';
const ADMIN_KEY = 'bb_admin_user';
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } },
};

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => store.get(TOKEN_KEY));
  const [admin, setAdmin] = useState(() => { try { return JSON.parse(store.get(ADMIN_KEY)); } catch { return null; } });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const clear = useCallback(() => {
    store.del(TOKEN_KEY); store.del(ADMIN_KEY);
    setToken(null); setAdmin(null);
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_BASE}/admin/login`, { email, password });
      const { token: t, admin: a } = res.data;
      store.set(TOKEN_KEY, t); store.set(ADMIN_KEY, JSON.stringify(a));
      setToken(t); setAdmin(a);
      return true;
    } catch (e) {
      setError(e.response
        ? (e.response.data?.message || `Login failed (error ${e.response.status}).`)
        : 'Cannot reach the API server. Make sure `npm run dev` is running in the server folder.');
      return false;
    } finally { setLoading(false); }
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API_BASE}/admin/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }); } catch { /* ignore */ }
    clear();
  }, [token, clear]);

  // Authenticated request helper — logs out automatically if the token has expired
  const api = useCallback(async (method, path, data = null, params = null) => {
    try {
      return await axios({ method, url: `${API_BASE}/admin${path}`, data, params, headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      if (e.response?.status === 401) clear();
      throw e;
    }
  }, [token, clear]);

  return (
    <AdminAuthContext.Provider value={{ token, admin, loading, error, login, logout, api, isLoggedIn: !!token }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);
